import { Injectable } from '@angular/core';
import { CrudApiProvider } from '../crud-api/crud-api';
import { UserProvider } from '../user/user';
import { EventEmitter } from '../../includes/eventEmitter';
import * as debug from 'debug';
import * as _ from 'lodash';
import { ConfigProvider } from '../config/config';
import { ToolsProvider } from '../tools/tools';
import { JobAdsProvider } from '../job-ads/job-ads';
import { AdsProvider } from '../ads/ads';
import { GeolocationProvider } from '../geolocation/geolocation';
import { SlottingType, getSlottingFallback } from './slotting-fallbacks';
import { LiveChatProvider } from '../live-chat/live-chat';

/*

  // Post Usage Notes ----------------

  import {
    PostsProvider,
    FeedPreset
  } from '../../../providers/posts/posts';

  constructor(
    public postProvider: PostsProvider
  ) {
    this.feed = this.postProvider.getFeed(FeedPreset.Community, {
      sortBy: 'score.overal:desc',  // default (also try 'dates.created:desc'
      query: {
        // additional query information if needed
        // example:
        //   {
        //      'dates.created': {
        //        $gte: moment().subtract(1, 'days').toISOString()
        //      }
        //   }
      }
    });

    // Can force it to load more (will load the first set right off the bat)
    this.feed.loadData().then(records => {
      // this will be all the records in total, not just the new ones
    });

    // can listen for events:

    this.feed.on('loading', () => {
      // do something
    });

    this.feed.on('doneLoading', () => {
      // do something
    });

    this.feed.on('records', (records) => {
      // new records were loaded
      // this will be all the records in total, not just the new ones
    });
  }

*/

export enum FeedPreset {
  All,
  Community,
  News,
  Jobs,
  UserActivity,
  CDLLife,
  LiveTrucking,
  TruckDriversUSA,
  FlaggedContent,
  UserPosts,
  Latest,
  Friends,
  Nearby,
  LiveChat,
  ThreadsFeed,
  YoutubeFeed,
}

export enum PostSubset {
  Multi,
  Community,
  CDLLife,
  LiveTrucking,
  Ad,
  Job,
  JobAds,
  Article,
  TruckDriversUSA,
  FeedEngagements,
  FlaggedEngagements,
  CommunityRecent,
  PlaceReview,
  AdSpec,
  Latest,
  Friends,
  Nearby,
  SponsoredPoll,
  CustomComponent,
  Thread,
  Youtube,
}

// This class can be instantiated more than once. Providers only have
// one instantiation
export class PostFeed extends EventEmitter {
  log = debug('debug:app:PostFeed');

  parent: PostsProvider;
  crudApiProvider: CrudApiProvider;
  userProvider: UserProvider;
  toolsProvider: ToolsProvider;
  jobAds: JobAdsProvider;
  adsProvider: AdsProvider;
  geolocation: GeolocationProvider;
  liveChat: LiveChatProvider;
  isLoading = true;
  inited = false;

  records = [];
  hasSeen = {};
  totalCount = 0;
  sponsoredPoll = [];

  opts: any = {
    maxCache: 20, // load no more than this at a time for one type
    sortBy: 'score.overall:desc',

    // extra query info
    query: {},

    // When true, the posts aren't technically sorted by the sort value.
    // instead, we slot the type of posts (cdllife, livetrucking, ad, etc)
    // in a particular order. Within those categories, each posts is sorted
    slotPosts: true,

    subset: [], // must be passed in
    excludeAppliedJobs: true,
    sortOrder: [], // must be passed in
    showAds: true,
    showSorted: false,
    maxInSortedLoad: 20,
    getId: _.property('_id'),
    removeBanned: false,
  };

  recordsCacheByType: any = {};
  skipsByType: any = {};
  noMoreRecordsByType: any = {};
  leftoverRecords = [];

  constructor(
    opts,
    parent,
    crudApiProvider?,
    userProvider?,
    tools?,
    jobAds?,
    adsProvider?,
    geolocation?,
    liveChat?
  ) {
    super();
    this.log('constructed', opts);
    this.opts = _.merge(this.opts, opts);
    this.parent = parent;
    this.crudApiProvider = crudApiProvider;
    this.userProvider = userProvider;
    this.toolsProvider = tools;
    this.adsProvider = adsProvider;
    this.geolocation = geolocation;
    this.jobAds = jobAds;
    this.liveChat = liveChat;
    if (!this.opts.showAds) {
      _.pull(this.opts.subset, PostSubset.Ad);
      _.pull(this.opts.sortOrder, PostSubset.Ad);
      _.pull(this.opts.subset, PostSubset.AdSpec);
      _.pull(this.opts.sortOrder, PostSubset.AdSpec);
    }

    // In this scenario, we only load two queries - one for all the right type
    // of posts, and one for ads
    if (!this.opts.slotPosts) {
      // Because of that, we won't put the Ad in the subset. We'll handle
      // differently
      _.pull(this.opts.subset, PostSubset.Ad);
      _.pull(this.opts.subset, PostSubset.AdSpec);

      this.opts.sortOrder = _.map(this.opts.sortOrder, o => {
        if (o === PostSubset.Ad) {
          return o;
        } else {
          return PostSubset.Multi;
        }
      });
      this.log('SORT Order: ', this.opts.sortOrder);
    }
    this.loadData();
  }

  private loadData(): Promise<any> {
    this.isLoading = true;
    this.emit('loading');
    this.log('load data');
    let promises;

    if (_.size(this.leftoverRecords)) {
      this.log('leftover records', _.size(this.leftoverRecords));

      // It's somewhat expensive to load from the DB, but if we're doing it
      // we can load a bunch. BUt then we don't want the phone to try to render
      // 150 posts at one time, so we limit it.
      //
      // If we did that, then we have "leftover"
      //
      // This only applies if things are loaded in a strictly sorted way. If
      // we are loading a predefined feed, it has a list of the order and the
      // number per load
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          let newRecords = this.leftoverRecords;
          this.leftoverRecords = newRecords.slice(this.opts.maxInSortedLoad);
          newRecords = newRecords.slice(0, this.opts.maxInSortedLoad);
          _.each(newRecords, r => {
            this.records.push(r);
          });
          this.emit('doneLoading');
          this.emit('records', this.records);

          this.emit('updateCount', this.totalCount);

          return resolve(this.records);
        }, 150);
      });
    } else {
    }

    if (this.opts.slotPosts) {
      this.log('loadData | slotPosts', this.opts.subset);

      promises = _.map(this.uniqueSubsets(this.opts.subset), t => {
        this.log('loadData | subset type', t);
        return this.loadForType(<any>t);
      });
      this.log('loadData | slotPosts - promises');
    } else {
      promises = [
        // load all post types in one query
        this.loadForType(
          PostSubset.Multi,
          this.queryForMultiType(this.opts.subset)
        ),
      ];

      this.log('loadData | no slotPosts - promises');
      if (this.opts.showAds) {
        promises.push(this.loadForType(PostSubset.Ad));
      }
    }

    return Promise.all(promises).then(
      d => {
        this.log('then() promises');
        this.appendSortedRecords();
        this.emit('doneLoading');
        this.emit('records', this.records);
        this.emit('updateCount', this.totalCount);
        return Promise.resolve(this.records);
      },
      e => {
        this.log('error', e);
        return Promise.reject(e);
      }
    );
  }

  private appendSortedRecords() {
    // The way we do this is by going through the "sortOrder" twice.
    this.log('caches here', _.cloneDeep(this.recordsCacheByType));

    let newRecords = [];
    let nonAds = 0;
    let lastRecord = _.last(this.records);

    if (this.opts.showSorted) {
      this.log('in here 1');
      // Here, we just show records in a sorted order, skipping the feed
      // ordering.
      // Doesn't work with ads (yet)

      _.each(this.recordsCacheByType, (list, type) => {
        // clear it out
        _.each(list, (r, i) => {
          this.log('NEW RECORD', i, _.cloneDeep(r));
          if (this.hasSeen[this.opts.getId(r)]) {
            return; // don't duplicate posts
          } else {
            nonAds++;
          }

          newRecords.push(r);
        });

        // clear it out
        this.recordsCacheByType[type] = [];
      });

      let sortInfo = this.opts.sortBy.split(':'),
        key = sortInfo[0],
        order = sortInfo[1] || 'asc';

      newRecords = _.sortBy(newRecords, <any>(r => {
        let val = _.get(r, key);

        if (/date/.test(key)) {
          val = new Date(val);
        }

        return val;
      }));

      if (order === 'desc') {
        newRecords = newRecords.reverse();
      }

      if (_.size(newRecords) > this.opts.maxInSortedLoad) {
        this.leftoverRecords = newRecords.slice(this.opts.maxInSortedLoad);
        newRecords = newRecords.slice(0, this.opts.maxInSortedLoad);
      }
    } else {
      _.each(this.opts.sortOrder, typeInfo => {
        typeInfo = this.convertSubset(typeInfo, true);
        const postType = typeInfo.type;

        this.log('appendSortedRecords', postType, typeInfo);

        const r = _.get(this.recordsCacheByType, postType, []).shift();

        if (r) {
          if (this.isAvertisementType(r.postType)) {
            if (this.isAvertisementType(_.get(lastRecord, 'postType'))) {
              return;
            }

            // Don't show ads when searching
            if (_.get(this.opts, 'query.$text')) {
              return;
            }

            // Never want to run out of ads!
            _.get(this.recordsCacheByType, postType, []).push(_.clone(r));
          } else if (postType === PostSubset.JobAds) {
            _.get(this.recordsCacheByType, postType, []).push(_.clone(r));
          } else if (
            this.hasSeen[this.opts.getId(r)] ||
            _.get(r, 'states.isPinned')
          ) {
            return; // don't duplicate posts
          } else {
            nonAds++;
          }

          newRecords.push((lastRecord = r));
          this.hasSeen[r._id] = true;
        }
      });
    }

    // prevent just adding ads when no more
    if (nonAds) {
      _.each(newRecords, r => {
        this.records.push(r);
      });
    }

    this.log('records', this.records);
  }

  private isAvertisementType(type) {
    return type === 'AD_SPEC' || type === 'AD';
  }

  private recordsNeededForType(type) {
    const order = this.opts.sortOrder;
    let count = 0;
    _.each(order, o => {
      if (_.get(o, 'type') === type || o === type) {
        count++;
      }
    });
    this.log('recordsNeededForType', count, this.opts.sortOrder, type);
    return count;
  }

  // if a queryOverride is provide, we use that instead
  // loadForType(type: PostSubset, queryOverride?) {
  private loadForType(typeInfo, queryOverride?) {
    this.log('loadForType | typeInfo', typeInfo);
    // this.log('loadForType | queryOverride', queryOverride);

    const type: PostSubset = _.get(this.convertSubset(typeInfo), 'type'),
      typeOptions = _.get(this.convertSubset(typeInfo), 'options');

    this.log('loadForType | typeInfo CONVERTED', type, typeOptions);

    let overridePromise;
    let threads;
    let url = '/api/posts';
    // this.log('loadForType | loading type', type, typeOptions);

    if (type === PostSubset.JobAds) {
      overridePromise = this.jobAds.getJobs(100).then(jobs => {
        return { data: jobs };
      });
    }

    // If we already have enough
    if (this.hasMoreRecordsOfType(type)) {
      this.log('loadForType | skipping');
      return Promise.resolve();
    }

    let sort = /random/.test(this.opts.sortBy)
        ? this.opts.sortBy
        : this.opts.sortBy + ',score.random',
      query: any = _.merge(
        queryOverride || this.queryForType(type),
        {
          // we used to only load like 8 more if we had 42, but it's
          // really expensive to hit hte API for just 8 records. So we
          // always  load 50
          limit: this.opts.maxCache,
          sortBy: sort,
          // sortBy: this.opts.sortBy,
        },
        this.opts.query,
        this.skipQuery(type)
      );
    // this.log('loadForType | query: ', query, type);
    // already has enough
    if (!query.limit) {
      return Promise.resolve();
    }

    switch (type) {
      case PostSubset.Ad:
        query = {
          postType: 'AD',
          limit: 200,
          $or: [
            { 'adInformation.adType': 'FEED' },
            { 'adInformation.adSource': { $ne: 'DIRECT_AD' } },
          ],
        };
        break;
      case PostSubset.Community:
        this.log('loadForType | type: ', type, query, this.opts);

        if (!_.get(query, 'user')) {
          if (_.get(query, 'reported')) {
          } else {
            const scoreUrl = '/api/feeds/score?limit=';

            overridePromise = this.crudApiProvider
              .post(
                '/api/feeds/score?limit=' +
                  this.opts.maxCache +
                  '&skip=' +
                  (_.get(this.skipsByType, type + '.skip') || 0),
                {
                  postImpressions: this.toolsProvider.getPostImpressions(),
                  postEngagements: this.toolsProvider.getPostEngagements(),
                  hashtags: query.hashtags,
                }
              )
              .catch(e => {
                return this.crudApiProvider.post(
                  scoreUrl +
                    this.opts.maxCache +
                    '&skip=' +
                    (_.get(this.skipsByType, type + '.skip') || 0),
                  {
                    postImpressions: {},
                  }
                );
              })
              .catch(e => {
                return Promise.resolve([]);
              });
          }
        } else {
          if (this.userProvider.get('_id') === query.user) {
            delete query.source;
          }
        }
        break;
      case PostSubset.AdSpec:
        overridePromise = Promise.resolve({
          data: _.range(this.opts.maxCache).map(d => {
            return {
              _id: Math.random(),
              postType: 'AD_SPEC',
              options: _.cloneDeep(typeOptions),
            };
          }),
        });
        break;
      case PostSubset.FeedEngagements:
        url = '/api/engagements';
        if (!query.user) {
          this.log('loadForType | FeedEngagements - no user provided in query');
          return;
        }
        break;
      case PostSubset.FlaggedEngagements:
        url = '/api/engagements';
        break;
      case PostSubset.CommunityRecent:
        if (!_.size(this.opts.query)) {
          overridePromise = this.crudApiProvider.post(
            '/api/feeds/recent?limit=' +
              this.opts.maxCache +
              '&skip=' +
              (_.get(this.skipsByType, type + '.skip') || 0)
          );
        } else {
          this.log('loadForType | CommunitiyRecent - resetting sortBy');
          query.sortBy = 'dates.created:desc';
        }
        break;
      case PostSubset.PlaceReview:
        this.log(
          'loadForType | PlaceReview - resetting sortBy for review to random'
        );
        query.sortBy = 'score.random';
        break;
      case PostSubset.Latest:
        overridePromise = this.crudApiProvider.post(
          '/api/feeds/recent?limit=' +
            this.opts.maxCache +
            '&skip=' +
            (_.get(this.skipsByType, type + '.skip') || 0),
          { hashtags: query.hashtags }
        );
        break;
      case PostSubset.Article:
        if (!_.get(this.opts, 'query.user')) {
          overridePromise = this.crudApiProvider
            .post(this.crudApiProvider.addQuery('/api/feeds/news', query))
            .catch(e => {
              return {
                data: [],
              };
            });
        }
        break;
      case PostSubset.Friends:
        overridePromise = this.crudApiProvider.post(
          '/api/feeds/friends?limit=' +
            this.opts.maxCache +
            '&skip=' +
            (_.get(this.skipsByType, type + '.skip') || 0),
          { hashtags: query.hashtags }
        );
        break;
      case PostSubset.Thread:
        overridePromise = this.crudApiProvider
          .post(
            '/api/feeds-comments/score?limit=' +
              this.opts.maxCache +
              '&skip=' +
              (_.get(this.skipsByType, type + '.skip') || 0),
            {
              postImpressions: this.toolsProvider.getPostImpressions(),
              postEngagements: this.toolsProvider.getPostEngagements(),
            }
          )
          .catch(e => {
            return Promise.resolve([]);
          });

        break;
      case PostSubset.Nearby:
        overridePromise = this.geolocation
          .getCurrentPosition({
            minLevel: 10,
            passRejection: true,
          })
          .then(res => {
            this.log(
              'loadForType | Nearby - got current position for nearby feed',
              res
            );

            const lon = _.get(res, 'longitude');
            const lat = _.get(res, 'latitude');

            return this.crudApiProvider.post(
              `/api/feeds/nearby?lon=${lon}&&lat=${lat}&limit=${
                this.opts.maxCache
              }&skip=${_.get(this.skipsByType, type + '.skip') || 0}`
            );
          })
          .catch(e => {
            console.error('GEOLOC ERROR', e);
          });

        break;
      case PostSubset.SponsoredPoll:
        overridePromise = this.crudApiProvider.post(
          `/api/feeds/sponsoredPolls`
        );
        break;
      case PostSubset.CustomComponent:
        //default to one per feed load if no limit specified
        let queryLimit = _.get(typeOptions, 'limit') || 1;
        this.log('loadForType | custom limit', queryLimit);
        if (typeOptions.componentType === 'LC_CHAT_WINDOW') {
          overridePromise = this.liveChat.getPublicRooms().then(rooms => {
            return {
              data: _.filter(_.sortBy(rooms, 'dates.updated'), (r) => !r.isClosed && !r.states.isClosed)
                .reverse()
                .map((room, i) => ({
                  _id: room?._id,
                  postType: 'CUSTOM_COMPONENT',
                  options: _.cloneDeep(typeOptions),
                  data: { room },
                })),
            };
          });
        } else {
          overridePromise = Promise.resolve({
            data: _.range(queryLimit).map((d, i) => ({
              _id: `CUSTOM-${typeOptions?.componentType}-${i}`,
              postType: 'CUSTOM_COMPONENT',
              options: _.cloneDeep(typeOptions),
            })),
          });
        }
        break;
      case PostSubset.Youtube:
        //default to one per feed load if no limit specified
        overridePromise = this.crudApiProvider
          .get('/api/posts', {
            postType: 'VIDEO',
            'videoInformation.youtubeId': { $exists: true },
          })
          .catch(e => {
            return Promise.resolve([]);
          });
        break;
      default:
        return { overridePromise, threads };
    }

    if (!overridePromise) {
      overridePromise = this.crudApiProvider.get(url, query).catch(e => {
        this.log('override promise get error: ', e);
      });
    }

    return overridePromise
      .then(res => {
        if (_.get(res, 'data')) {
          // add to API instead (feed-comments)
          // if (type === PostSubset.Thread) {
          //   res.data.forEach((item: any) => {
          //     item.source = 'THREAD';
          //     item.requestUserId = this.userProvider.info._id;
          //     if (item.replies.length) {
          //       let replies = item.replies;

          //       replies.forEach(reply => {
          //         let friendship = false;
          //         this.crudApiProvider
          //           .get(
          //             `/api/friends/${this.userProvider.info._id}/${reply.user}`
          //           )
          //           .then(res => {
          //             if (res.data != null) {
          //               if (
          //                 res.data.accepted === true &&
          //                 res.data.userA != res.data.userB
          //               ) {
          //                 friendship = true;
          //               } else {
          //                 friendship = false;
          //               }
          //             } else {
          //               friendship = false;
          //             }
          //             reply.friendship = friendship;
          //           })
          //           .catch(e => {
          //             this.log('error', e);
          //           });
          //       });
          //     }
          //   });
          // }
          if (type === PostSubset.Ad) {
            res.data = this.toolsProvider.sortAdsByWeight(
              this.adsProvider.removeAdsSeenPastSixHours(res.data)
            );
            this.log('loadForType | Ad - sorted ads', res.data);
          } else if (query.sortBy === 'score.random') {
            res.data = _.shuffle(res.data);
          }
          const cacheType =
            type === PostSubset.CustomComponent
              ? typeOptions?.componentType
              : type;

          this.log('loadForType | cacheType', cacheType);
          this.recordsCacheByType[cacheType] =
            this.recordsCacheByType[cacheType] || [];

          this.addSkip(cacheType, res);

          if (this.opts.removeBanned) {
            res.data = this.removeBanned(res.data);
          }

          if (cacheType !== PostSubset.Ad) {
            this.totalCount += _.size(res.data);
          }

          let queryLimit = _.get(typeOptions, 'limit') || 1;
          _.each(res.data, d => {
            if (
              _.get(typeOptions, 'limit') &&
              _.size(this.recordsCacheByType[cacheType]) >= queryLimit
            ) {
              return;
            } else {
              this.recordsCacheByType[cacheType].push(d);
            }
          });
          this.log(
            'loadForType | recordsCacheByType',
            cacheType,
            this.recordsCacheByType[cacheType]
          );
        }
      })
      .catch(e => {
        this.log('error: ', e);
        return Promise.reject(e);
      });
  }

  private removeBanned(content) {
    return _.filter(content, c => {
      if (this.userProvider.userShouldSeeContent(c)) {
        return c;
      }
    });
  }

  private hasMoreRecordsOfType(type: PostSubset) {
    if (this.noMoreRecordsByType[type]) {
      return true;
    }
    if (_.isUndefined(this.recordsCacheByType[type])) {
      return false;
    } else if (
      _.size(this.recordsCacheByType[type]) >= this.recordsNeededForType(type)
    ) {
      this.log('we have enough records', this.recordsCacheByType[type]);
      return true;
    } else {
      return false;
    }
  }

  private skipQuery(type) {
    return _.get(this.skipsByType, type, {});
  }

  private addSkip(type, res) {
    if (!_.get(res, 'data')) {
      return (this.noMoreRecordsByType[type] = true);
    }
    const more = _.size(_.get(res, 'data'));

    if (/dates\./.test(this.opts.sortBy)) {
      this.log('we are sorting by date');
      const sortKey = String(_.first(this.opts.sortBy.split(':')) || ''),
        lastDate = _.get(_.last(res.data), sortKey);

      if (lastDate) {
        let q = {};
        q[sortKey] = { $lt: lastDate };
        _.set(this.skipsByType, type, q);
      }
    } else {
      _.set(this.skipsByType, type, {
        skip: _.get(this.skipsByType, `${type}.skip`, 0) + more,
      });
    }

    if (more < this.opts.maxCache) {
      this.noMoreRecordsByType[type] = true;
    }
  }

  private queryForMultiType(types: PostSubset[]) {
    this.log('QUERY FOR MULTI TYPE');
    let sources = [],
      postTypes = [],
      engagementType = [],
      query: any = {};

    types = _.uniq(types);

    _.each(types, type => {
      if (type === PostSubset.Community) {
        sources.push('COMMUNITY');
      } else if (type === PostSubset.CommunityRecent) {
        sources.push('COMMUNITY');
      } else if (type === PostSubset.CDLLife) {
        sources.push('CDLLIFE');
      } else if (type === PostSubset.LiveTrucking) {
        sources.push('LIVETRUCKING');
      } else if (type === PostSubset.TruckDriversUSA) {
        sources.push('TRUCKDRIVERSUSA');
      } else if (type === PostSubset.Ad) {
        postTypes.push('AD');
      } else if (type === PostSubset.Job) {
        postTypes.push('JOB');
      } else if (type === PostSubset.Article) {
        postTypes.push('ARTICLE');
      } else if (type === PostSubset.SponsoredPoll) {
        postTypes.push('SPONSORED_POLLL');
      } else if (type === PostSubset.FeedEngagements) {
        postTypes.push('FEED_ENGAGEMENTS');
      } else if (type === PostSubset.CustomComponent) {
        postTypes.push('CUSTOM_COMPONENT');
      } else if (type === PostSubset.Thread) {
        engagementType.push('THREAD');
      } else if (type === PostSubset.Youtube) {
        engagementType.push('YOUTUBE');
      }
    });

    if (_.size(sources) && _.size(postTypes)) {
      query.$or = [
        { source: { $in: sources } },
        { postType: { $in: postTypes } },
      ];
    } else if (_.size(sources)) {
      query.source = { $in: sources };
    } else if (_.size(postTypes)) {
      query.postType = { $in: postTypes };
    }

    return query;
  }

  private queryForType(type: PostSubset) {
    if (type === PostSubset.Community) {
      return { source: 'COMMUNITY' };
    } else if (type === PostSubset.CommunityRecent) {
      return { source: 'COMMUNITY' };
    } else if (type === PostSubset.CDLLife) {
      return { source: 'CDLLIFE' };
    } else if (type === PostSubset.LiveTrucking) {
      return { source: 'LIVETRUCKING' };
    } else if (type === PostSubset.TruckDriversUSA) {
      return { source: 'TRUCKDRIVERSUSA' };
    } else if (type === PostSubset.Article) {
      return { postType: 'ARTICLE' };
    } else if (type === PostSubset.Ad) {
      return {
        postType: 'AD',
        'adInformation.adType': 'FEED',
      };
    } else if (type === PostSubset.Job) {
      return _.merge(
        {
          postType: 'JOB',
        },
        this.opts.excludeAppliedJobs
          ? this.opts.user.filterAppliedCompanies()
          : {}
      );
    } else if (type === PostSubset.FeedEngagements) {
      return {
        resolve: { parents: true, userEngagements: true },
        engagementType: {
          $in: ['LIKE', 'COMMENT'],
        },
      };
    } else if (type === PostSubset.FlaggedEngagements) {
      return {
        resolve: { parents: true, userEngagements: true },
        engagementType: {
          $in: ['COMMENT'],
        },
      };
    } else if (type === PostSubset.PlaceReview) {
      return { postType: 'REVIEW' };
    } else if (type === PostSubset.SponsoredPoll) {
      return {
        postType: 'MESSAGE',
        'pollInformation.sponsored': true,
      };
    } else if (type === PostSubset.CustomComponent) {
      return {
        postType: 'CUSTOM_COMPONENT',
      };
    } else if (type === PostSubset.Thread) {
      return { engagementType: 'THREAD', userEngagements: true };
    } else if (type === PostSubset.Youtube) {
      return {
        postType: 'VIDEO',
        'videoInformation.youtubeId': { $exists: true },
      };
    }
  }

  private convertSubset(s, typeOverride?) {
    if (typeof s === 'number') {
      return {
        type: s,
        options: {},
      };
    } else if (
      typeOverride &&
      (s?.type === PostSubset.CustomComponent ||
        s === PostSubset.CustomComponent)
    ) {
      return {
        type: s?.options?.subsetType || PostSubset.CustomComponent,
        options: s?.options,
      };
    } else {
      return s;
    }
  }

  private uniqueSubsets(subsets) {
    return _.uniqBy(
      _.map(subsets, s => this.convertSubset(s)),
      d => {
        this.log('uniqueSubsets', d['type'] + JSON.stringify(d['options']));
        return d['type'] + JSON.stringify(d['options']);
      }
    );
  }
}

@Injectable()
export class PostsProvider {
  log = debug('app:PostsProvider');

  slotting: any;

  constructor(
    public crudApiProvider: CrudApiProvider,
    public user: UserProvider,
    public config: ConfigProvider,
    public tools: ToolsProvider,
    public jobAds: JobAdsProvider,
    public adsProvider: AdsProvider,
    public geolocation: GeolocationProvider,
    public liveChat: LiveChatProvider
  ) {
    this.log('constructed');
    this.slotting = this.config.get('contentSlotting');
    this.log('SLOTTING', this.slotting);
  }

  getFeed(preset: FeedPreset = FeedPreset.All, opts?): PostFeed {
    this.log('FEED OPTIONS: ', opts, preset);

    this.slotting = this.config.get('contentSlotting');
    const adInterval = _.get(this, 'slotting.adInterval', 10);

    this.log('SLOTTING', this.slotting);
    opts = opts || {};

    opts.user = this.user;

    // TODO: more feeds
    // TODO: eventually, sort orders will be pulled through
    //       from admin configs

    switch (preset) {
      case FeedPreset.All:
        if (_.startsWith(_.get(opts, 'sortBy'), 'dates.created')) {
          opts.removeBanned = true;
        } else {
          opts.sortOrder = this.mapSlottingConfigTypes(
            _.size(opts.query.hashtags)
              ? this.config.get('contentSlottingHashtags')
              : this.slotting.slots,
            _.size(opts.query.hashtags) ? 'hashtags' : 'all'
          );

          opts.removeBanned = true;

          opts.subset = _.uniqBy(opts.sortOrder, d => {
            return d['type'] + JSON.stringify(d['options']);
          });

          // at least 10
          opts.sortOrder = this.extendSortOrder(opts.sortOrder);

          this.log('PRESET | ALL', opts);
        }
        break;
      case FeedPreset.Community:
        opts.subset = [PostSubset.CommunityRecent];

        opts.sortOrder = [PostSubset.CommunityRecent];
        while (_.size(opts.sortOrder) < adInterval) {
          opts.sortOrder.push(PostSubset.CommunityRecent);
        }

        // at least 10
        opts.sortOrder = this.extendSortOrder(opts.sortOrder);

        this.log('PRESET | COMMUNITY', opts.sortOrder);
        break;
      case FeedPreset.UserActivity:
        this.log('UserActivity', preset);
        // Allows us to pass in only engagements
        if (!_.size(opts.subset)) {
          opts.subset = [PostSubset.Community, PostSubset.FeedEngagements];
        }
        this.log('HELLO QUERY', preset);
        this.log('HELLO QUERY', opts);

        if (_.get(opts, 'query.user') === 'friends') {
          opts.sortOrder = [PostSubset.Community];
        } else {
          // doesn't matter because dates.created will sort
          opts.sortOrder = [PostSubset.FeedEngagements, PostSubset.Community];
        }

        opts.showSorted = true;
        opts.sortBy = opts.sortBy || 'dates.created:desc';
        break;
      case FeedPreset.UserPosts:
        this.log('UserPosts', preset);
        // Allows us to pass in only engagements
        // if (!_.size(opts.subset)) {
        //   opts.subset = [PostSubset.Community];
        // }
        // doesn't matter because dates.created will sort
        opts.subset = [PostSubset.Community];
        opts.sortOrder = [PostSubset.Community];

        opts.sortOrder = this.extendSortOrder(opts.sortOrder);
        this.log('opts for userPosts', opts);
        // opts.showSorted = false;
        // opts.sortBy = opts.sortBy || 'dates.created:desc';
        break;
      case FeedPreset.News:
        opts.subset = [PostSubset.Article];

        opts.sortOrder = [PostSubset.Article];

        // at least 10
        opts.sortOrder = this.extendSortOrder(opts.sortOrder);

        this.log('PRESET | NEWS', opts.sortOrder);
        break;
      case FeedPreset.Jobs:
        opts.subset = [PostSubset.Job];

        opts.sortOrder = [
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
          PostSubset.Job,
        ];
        break;
      case FeedPreset.CDLLife:
        opts.subset = [PostSubset.CDLLife];

        opts.sortOrder = [
          PostSubset.CDLLife,
          PostSubset.CDLLife,
          PostSubset.CDLLife,
          PostSubset.CDLLife,
          PostSubset.Ad,
          PostSubset.CDLLife,
          PostSubset.CDLLife,
          PostSubset.CDLLife,
          PostSubset.CDLLife,
          PostSubset.Ad,
        ];
        break;
      case FeedPreset.LiveTrucking:
        opts.subset = [PostSubset.LiveTrucking];

        opts.sortOrder = [
          PostSubset.LiveTrucking,
          PostSubset.LiveTrucking,
          PostSubset.LiveTrucking,
          PostSubset.LiveTrucking,
          PostSubset.Ad,
          PostSubset.LiveTrucking,
          PostSubset.LiveTrucking,
          PostSubset.LiveTrucking,
          PostSubset.LiveTrucking,
          PostSubset.Ad,
        ];
        break;
      case FeedPreset.TruckDriversUSA:
        opts.sortOrder = [
          PostSubset.TruckDriversUSA,
          PostSubset.TruckDriversUSA,
          PostSubset.TruckDriversUSA,
          PostSubset.TruckDriversUSA,
          PostSubset.Ad,
          PostSubset.TruckDriversUSA,
          PostSubset.TruckDriversUSA,
          PostSubset.TruckDriversUSA,
          PostSubset.TruckDriversUSA,
          PostSubset.Ad,
        ];
        break;
      case FeedPreset.FlaggedContent:
        opts.subset = [PostSubset.Community, PostSubset.FlaggedEngagements];

        // doesn't matter because dates.created will sort
        opts.sortOrder = [PostSubset.FlaggedEngagements, PostSubset.Community];

        opts.showSorted = true;
        // opts.sortBy = opts.sortBy || 'dates.created:asc'; //'stats.reports';
        opts.sortBy = opts.sortBy || 'dates.created:desc'; //'stats.reports';
        opts.query = _.merge(opts.query, {
          reported: true, // query shortcut in the API
        });
        break;
      case FeedPreset.Latest:
        opts.subset = [PostSubset.Latest, PostSubset.AdSpec];
        opts.sortOrder = [PostSubset.Latest];

        while (_.size(opts.sortOrder) < adInterval) {
          opts.sortOrder.push(PostSubset.Latest);
        }
        opts.sortOrder.push(PostSubset.AdSpec);

        // at least 10
        opts.sortOrder = this.extendSortOrder(opts.sortOrder);

        this.log('PRESET | LATEST', opts.sortOrder);
        break;
      case FeedPreset.Friends:
        opts.subset = [PostSubset.Friends, PostSubset.AdSpec];
        opts.sortOrder = [PostSubset.Friends];

        while (_.size(opts.sortOrder) < adInterval) {
          opts.sortOrder.push(PostSubset.Friends);
        }
        opts.sortOrder.push(PostSubset.AdSpec);

        // at least 10
        opts.sortOrder = this.extendSortOrder(opts.sortOrder);

        this.log('PRESET | FRIENDS', opts.sortOrder);
        break;
      case FeedPreset.Nearby:
        opts.subset = [PostSubset.Nearby, PostSubset.AdSpec];
        opts.sortOrder = [PostSubset.Nearby];

        while (_.size(opts.sortOrder) < adInterval) {
          opts.sortOrder.push(PostSubset.Nearby);
        }
        opts.sortOrder.push(PostSubset.AdSpec);

        // at least 10
        opts.sortOrder = this.extendSortOrder(opts.sortOrder);

        this.log('PRESET | Nearby', opts.sortOrder);
        break;
      case FeedPreset.LiveChat:
        // console.log('LIVE CHAT PRESET CASE CHECK');
        break;
      case FeedPreset.ThreadsFeed:
        opts.subset = [PostSubset.Thread, PostSubset.AdSpec];
        opts.sortOrder = [PostSubset.Thread];

        while (_.size(opts.sortOrder) < adInterval) {
          opts.sortOrder.push(PostSubset.Thread);
        }
        opts.sortOrder.push(PostSubset.AdSpec);

        opts.removeBanned = true;
        this.log('PRESET | THREADS', opts);

        break;
      case FeedPreset.YoutubeFeed:
        opts.subset = [PostSubset.Youtube, PostSubset.AdSpec];
        opts.sortOrder = [PostSubset.Youtube];

        while (_.size(opts.sortOrder) < adInterval) {
          opts.sortOrder.push(PostSubset.Youtube);
        }
        opts.sortOrder.push(PostSubset.AdSpec);

        opts.removeBanned = true;
        this.log('PRESET | YOUTUBE', opts);

        break;
      default:
        this.log('No Sort Case for option');
        break;
    }

    opts.sortOrder = _.map(opts.sortOrder, d => {
      if (d.type) {
        return d;
      } else {
        return {
          type: d,
          options: {},
        };
      }
    });

    this.log('post feed opts', opts);
    return new PostFeed(
      opts,
      this,
      this.crudApiProvider,
      this.user,
      this.tools,
      this.jobAds,
      this.adsProvider,
      this.geolocation,
      this.liveChat
    );
  }

  mapSlottingConfigTypes(slots: any[], slottingType: SlottingType) {
    this.log('mapSlottingConfigTypes | slots', slots);

    if (!slots) {
      slots = _.get(getSlottingFallback(slottingType), 'slots') || [
        { type: 'COMMUNITY' },
      ];
    }
    return _.map(slots, d => {
      let type =
        {
          COMMUNITY: PostSubset.Community,
          COMMUNITY_RECENT: PostSubset.CommunityRecent,
          LIVETRUCKING: PostSubset.LiveTrucking,
          CDLLIFE: PostSubset.CDLLife,
          TRUCKDRIVERSUSA: PostSubset.TruckDriversUSA,
          FEED_ENGAGEMENTS: PostSubset.FeedEngagements,
          AD: PostSubset.Ad,
          CDLLIFE_DEDICATED_JOBS: PostSubset.JobAds,
          NEWS: PostSubset.Article,
          PLACE_REVIEW: PostSubset.PlaceReview,
          AD_SPEC: PostSubset.AdSpec,
          SPONSORED_POLL: PostSubset.SponsoredPoll,
          CUSTOM_COMPONENT: PostSubset.CustomComponent,
          THREAD: PostSubset.Thread,
          YOUTUBE: PostSubset.Youtube,
        }[d && d.type] || PostSubset.Community;

      return {
        type: type,
        options: d.options || {},
      };
    });
  }

  private extendSortOrder(order) {
    this.log('order: ', order);
    while (_.size(order) < 10) {
      order = _.concat(_.slice(order), _.slice(order));
    }
    return order;
  }

  private convertSubset(s, typeOverride?) {
    if (typeof s === 'number') {
      return {
        type: s,
        options: {},
      };
    } else if (
      typeOverride &&
      (s?.type === PostSubset.CustomComponent ||
        s === PostSubset.CustomComponent)
    ) {
      return {
        type: s?.options?.subsetType || PostSubset.CustomComponent,
        options: s?.options,
      };
    } else {
      return s;
    }
  }

  uniqueSubsets(subsets) {
    return _.uniqBy(
      _.map(subsets, s => this.convertSubset(s)),
      d => {
        const x = d['type'] + JSON.stringify(d['options']);
        this.log('uniqueSubsets', x);
        return d['type'] + JSON.stringify(d['options']);
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/posts/posts.ts